import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Locale } from 'date-fns';
import en from '../lang/en/portal.json';
import {
  LANG_STORAGE_KEY,
  PortalLang,
  detectLanguage,
  normaliseLang,
  splitLangPrefix,
} from './detect';

/**
 * The booking portal's translations (QA 2026-09-25 #6).
 *
 * i18next + react-i18next. English is bundled and initialised synchronously,
 * so every tenant renders exactly the strings it rendered before this file
 * existed, on the first paint, and every test that asserts English copy
 * keeps passing without any setup. French and Spanish are separate webpack
 * chunks (`lang/fr/portal.json` + the date-fns locale, via i18n/bundles/*),
 * fetched only when the Aperio preset picks that language: Go Oil never
 * downloads them.
 *
 * Only the Aperio preset ever switches language (initPortalLanguage, called
 * from index.tsx for that preset). For every other tenant the language is
 * `en`, the document's `lang` attribute is left as index.html has it, and
 * nothing is read from or written to storage.
 */

i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: 'en',
  fallbackLng: 'en',
  supportedLngs: ['en', 'fr', 'es'],
  interpolation: { escapeValue: false },
  // Synchronous init: the EN resources are in memory already.
  initImmediate: false,
  returnNull: false,
  react: { useSuspense: false },
});

export type { PortalLang } from './detect';

/** `<html lang>` per language: the marketing site's own codes (util.js). */
export const HTML_LANG: Record<PortalLang, string> = {
  en: 'en',
  fr: 'fr-CA',
  es: 'es',
};

/** Intl locale per language, for numbers and dates. */
export const INTL_LOCALE: Record<PortalLang, string> = {
  en: 'en-CA',
  fr: 'fr-CA',
  es: 'es-419',
};

type Bundle = {
  messages: Record<string, unknown>;
  dateLocale: Locale;
};

const LOADERS: Record<Exclude<PortalLang, 'en'>, () => Promise<Bundle>> = {
  fr: () => import(/* webpackChunkName: "portal-fr" */ './bundles/fr')
    .then((m) => m.default),
  es: () => import(/* webpackChunkName: "portal-es" */ './bundles/es')
    .then((m) => m.default),
};

const dateLocales: Partial<Record<PortalLang, Locale>> = {};

let active = false;
let brandForTitle: string | null = null;

/** True once the Aperio preset has switched the portal's i18n on. */
export function isPortalI18nActive(): boolean {
  return active;
}

/** The language on screen right now. Always `en` outside the preset. */
export function portalLanguage(): PortalLang {
  return normaliseLang(i18n.language) || 'en';
}

/** date-fns locale for the language on screen (undefined = date-fns EN). */
export function portalDateLocale(): Locale | undefined {
  return dateLocales[portalLanguage()];
}

function readStoredLanguage(): string | null {
  try {
    return window.localStorage.getItem(LANG_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Remembers an explicit choice. Best effort: storage can be blocked. */
export function persistLanguage(lang: PortalLang): void {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // private window / blocked storage: the choice lasts this page only
  }
}

async function ensureBundle(lang: PortalLang): Promise<boolean> {
  if (lang === 'en' || i18n.hasResourceBundle(lang, 'translation')) {
    return true;
  }
  try {
    const bundle = await LOADERS[lang]();
    i18n.addResourceBundle(lang, 'translation', bundle.messages, true, true);
    dateLocales[lang] = bundle.dateLocale;
    return true;
  } catch {
    // A chunk that does not arrive (offline, a deploy mid-visit) leaves the
    // portal in English, with <html lang> saying so.
    return false;
  }
}

/**
 * Switches the portal to `lang` in place: no navigation, so the booking in
 * progress (the Redux store) is untouched. `<html lang>` follows the content
 * that is actually shown - if the bundle cannot load, that is English.
 */
export async function setPortalLanguage(
  lang: PortalLang,
  { persist = false }: { persist?: boolean } = {},
): Promise<PortalLang> {
  const loaded = await ensureBundle(lang);
  const shown: PortalLang = loaded ? lang : 'en';
  await i18n.changeLanguage(shown);
  document.documentElement.lang = HTML_LANG[shown];
  // The tab title bootTheme set ("Book Online | Aperio"), in that language.
  if (brandForTitle) {
    document.title = i18n.t('meta.bookingTitle', { brand: brandForTitle });
  }
  if (persist && loaded) persistLanguage(lang);
  return shown;
}

/**
 * Aperio preset boot: pick the language (i18n/detect.ts), fold a `/fr/...`
 * or `/es/...` path onto the portal's own route (keeping the query and the
 * hash), and load that language before the first render so a French visitor
 * never sees a flash of English. An explicit `?lang` or path prefix is
 * remembered for the next visit; a browser guess is not.
 */
export async function initPortalLanguage(
  brandName?: string | null,
): Promise<PortalLang> {
  active = true;
  brandForTitle = brandName || null;
  const { pathname, search, hash } = window.location;
  let navigatorLanguages: readonly string[] = [];
  if (typeof navigator !== 'undefined') {
    navigatorLanguages = navigator.languages?.length
      ? navigator.languages
      : [navigator.language];
  }
  const { lang, source } = detectLanguage({
    search,
    pathname,
    stored: readStoredLanguage(),
    navigatorLanguages,
  });

  const prefix = splitLangPrefix(pathname);
  if (prefix.lang) {
    try {
      window.history.replaceState(
        window.history.state,
        '',
        `${prefix.rest}${search}${hash}`,
      );
    } catch {
      // History API refused: the route simply stays as it is.
    }
  }

  return setPortalLanguage(lang, {
    persist: source === 'query' || source === 'path',
  });
}

export default i18n;
